import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  useTheme,
  useMediaQuery,
  Divider,
  FormControlLabel,
  MenuItem,
  RadioGroup,
  Radio,
  Select,
  Popper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const daysOfWeek = ["s", "m", "t", "w", "th", "f", "sa"];
const dayNamesMap = {
  s: "sunday",
  m: "monday",
  t: "tuesday",
  w: "wednesday",
  th: "thursday",
  f: "friday",
  sa: "saturday",
};

// Mapping for API payload
const dayKeyToApiMap = {
  s: "S",
  m: "M",
  t: "T",
  w: "W",
  th: "Th",
  f: "F",
  sa: "Sa",
};

function WorkHoursDialog({
  open,
  onClose,
  locationData,
  setValue,
  watch_location,
  selectedLocationIndex,
  trigger,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize state with existing data if available
  const [selectedDays, setSelectedDays] = useState([]);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const [customEnabled, setCustomEnabled] = useState(false);
  const [customTimes, setCustomTimes] = useState({});
  const [openAdvance, setOpenAdvance] = useState(false);
  const [repeatType, setRepeatType] = useState("days");
  const [repeatOnDays, setRepeatOnDays] = useState([]);
  const [endType, setEndType] = useState("never");
  const [endDate, setEndDate] = useState("");
  const [occurrenceCount, setOccurrenceCount] = useState(1);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [anchorEl, setAnchorEl] = useState(null);
  const openDatePicker = Boolean(anchorEl);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [endDateAnchorEl, setEndDateAnchorEl] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(dayjs());
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [tempStartTime, setTempStartTime] = useState("08:00");
  const [tempEndTime, setTempEndTime] = useState("20:00");
  const [displayStartTime, setDisplayStartTime] = useState("8:00 am");
  const [displayEndTime, setDisplayEndTime] = useState("8:00 pm");
  const [repeatEveryValue, setRepeatEveryValue] = useState(1);
  const [calculatedOccurrences, setCalculatedOccurrences] = useState(0);

  // Initialize state with existing working hours data when dialog opens
  useEffect(() => {
    if (open) {
      // Always reset to defaults first
      setSelectedDays(daysOfWeek);
      setStartTime("08:00");
      setEndTime("17:00");
      setCustomEnabled(false);
      setCustomTimes({});
      setOpenAdvance(false);
      setRepeatType("days");
      setRepeatOnDays([]);
      setEndType("never");
      setEndDate("");
      setOccurrenceCount(1);
      setSelectedDate(dayjs());
      setSelectedEndDate(dayjs());
      setDisplayStartTime("8:00 am");
      setDisplayEndTime("8:00 pm");
      setRepeatEveryValue(1);

      // If existing working hours exist, load them
      if (locationData?.workingHours && Object.keys(locationData.workingHours).length > 0) {
        const workingHours = locationData.workingHours;

        // Load existing data based on schedule type
        if (workingHours.scheduleType === "standard") {
          if (workingHours.workingDays) {
            // Convert API format back to internal format
            const internalDays = workingHours.workingDays
              .map((day) => {
                return Object.keys(dayKeyToApiMap).find(
                  (key) => dayKeyToApiMap[key] === day
                );
              })
              .filter((day) => day !== undefined); // Only include days that exist in mapping
            setSelectedDays(internalDays);
            setRepeatOnDays(internalDays);
          }
          if (workingHours.standardWorkHours) {
            setDisplayStartTime(workingHours.standardWorkHours.startTime);
            setDisplayEndTime(workingHours.standardWorkHours.endTime);
            // Convert display time back to 24hr format for inputs
            setStartTime(
              convertDisplayTimeTo24Hr(workingHours.standardWorkHours.startTime)
            );
            setEndTime(
              convertDisplayTimeTo24Hr(workingHours.standardWorkHours.endTime)
            );
          }
        } else if (workingHours.scheduleType === "custom") {
          setCustomEnabled(true);
          if (workingHours.customSchedule) {
            const customTimesData = {};
            Object.keys(workingHours.customSchedule).forEach((day) => {
              customTimesData[day] = {
                start: convertDisplayTimeTo24Hr(
                  workingHours.customSchedule[day].startTime
                ),
                end: convertDisplayTimeTo24Hr(
                  workingHours.customSchedule[day].endTime
                ),
              };
            });
            setCustomTimes(customTimesData);
            setSelectedDays(
              Object.keys(workingHours.customSchedule)
                .map((day) => {
                  return Object.keys(dayNamesMap).find(
                    (key) => dayNamesMap[key] === day
                  );
                })
                .filter((day) => day !== undefined) // Only include days that exist in mapping
            );
            const customInternalDays = Object.keys(workingHours.customSchedule)
              .map((day) => {
                return Object.keys(dayNamesMap).find(
                  (key) => dayNamesMap[key] === day
                );
              })
              .filter((day) => day !== undefined);
            setSelectedDays(customInternalDays);
            setRepeatOnDays(customInternalDays);
          }
        } else if (workingHours.scheduleType === "advanced") {
          setOpenAdvance(true);
          if (workingHours.advancedSettings) {
            const advanced = workingHours.advancedSettings;
            if (advanced.startDate) {
              setSelectedDate(dayjs(advanced.startDate));
            }
            if (advanced.startEndTime) {
              setDisplayStartTime(advanced.startEndTime.startTime);
              setDisplayEndTime(advanced.startEndTime.endTime);
              setStartTime(
                convertDisplayTimeTo24Hr(advanced.startEndTime.startTime)
              );
              setEndTime(
                convertDisplayTimeTo24Hr(advanced.startEndTime.endTime)
              );
            }
            if (advanced.customRecurrence) {
              const recurrence = advanced.customRecurrence;
              if (recurrence.repeatEvery)
                setRepeatEveryValue(recurrence.repeatEvery);
              if (recurrence.repeatUnit) setRepeatType(recurrence.repeatUnit);
              if (recurrence.endsOn) {
                setEndType(recurrence.endsOn.type);
                if (
                  recurrence.endsOn.type === "after" &&
                  recurrence.endsOn.occurrences
                ) {
                  setOccurrenceCount(recurrence.endsOn.occurrences);
                }
                if (recurrence.endsOn.type === "on" && recurrence.endsOn.date) {
                  setSelectedEndDate(dayjs(recurrence.endsOn.date));
                  setEndDate(recurrence.endsOn.date);
                }
              }
            }
          }
          if (workingHours.workingDays) {
            const internalDays = workingHours.workingDays
              .map((day) => {
                return Object.keys(dayKeyToApiMap).find(
                  (key) => dayKeyToApiMap[key] === day
                );
              })
              .filter((day) => day !== undefined); // Only include days that exist in mapping
            setSelectedDays(internalDays);
            setRepeatOnDays(internalDays);
          }
        }
      } else {
        setSelectedDays(daysOfWeek);
        setRepeatOnDays(daysOfWeek);
      }
    }
  }, [open, locationData]);

  useEffect(() => {
    if (selectedDate && selectedEndDate) {
      // If endType is "after", use the selected occurrenceCount
      if (endType === "after") {
        setCalculatedOccurrences(occurrenceCount);
      } else if (endType === "on") {
        // Calculate occurrences only for "on" end type
        const calculated = calculateOccurrences(
          selectedDate,
          selectedEndDate,
          repeatType,
          repeatEveryValue,
          repeatOnDays
        );
        setCalculatedOccurrences(calculated);
      }
    }
  }, [
    selectedDate,
    selectedEndDate,
    repeatType,
    repeatEveryValue,
    repeatOnDays,
    endType,
    occurrenceCount,
  ]);

  const convertDisplayTimeTo24Hr = (displayTime) => {
    if (!displayTime) return "08:00";
    const match = displayTime.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
    if (!match) return "08:00";

    let [, hours, minutes, period] = match;
    hours = parseInt(hours);
    if (period.toLowerCase() === "pm" && hours !== 12) hours += 12;
    if (period.toLowerCase() === "am" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleDayRepeatOnDays = (day) => {
    setRepeatOnDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleCustomTimeChange = (day, type, value) => {
    setCustomTimes((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [type]: value,
      },
    }));
  };

  const handleAdvanceToggle = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      setOpenAdvance(true);
      setCustomEnabled(false);
      setRepeatOnDays(selectedDays);
    } else {
      setOpenAdvance(false);
    }
  };

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEndDateIconClick = (event) => {
    setEndDateAnchorEl(event.currentTarget);
  };

  const handleEndDateClose = () => {
    setEndDateAnchorEl(null);
  };

  const formatTo12Hour = (time24) => {
    if (!time24) return ""; // Return empty string if no time provided
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleTimeClick = () => {
    setTimePickerOpen(true);
    setTempStartTime(startTime);
    setTempEndTime(endTime);
  };

  const handleTimeConfirm = () => {
    setStartTime(tempStartTime);
    setEndTime(tempEndTime);
    setDisplayStartTime(formatTo12Hour(tempStartTime));
    setDisplayEndTime(formatTo12Hour(tempEndTime));
    setTimePickerOpen(false);
  };

  const handleTimeCancel = () => {
    setTempStartTime(startTime);
    setTempEndTime(endTime);
    setTimePickerOpen(false);
  };

  // Add this function after imports
  const calculateOccurrences = (
    startDate,
    endDate,
    repeatUnit,
    repeatEvery,
    selectedDays // Add selectedDays parameter
  ) => {
    if (!startDate || !endDate) return 1;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (repeatUnit) {
      case "days": {
        const days = end.diff(start, "days") + 1;
        return Math.ceil(days / repeatEvery);
      }
      case "week": {
        let occurrences = 0;
        let currentDate = start;

        // Create a Set of selected day numbers (0-6, where 0 is Sunday)
        const selectedDayNumbers = new Set(
          selectedDays.map((day) => {
            switch (day) {
              case "s":
                return 0;
              case "m":
                return 1;
              case "t":
                return 2;
              case "w":
                return 3;
              case "th":
                return 4;
              case "f":
                return 5;
              case "sa":
                return 6;
              default:
                return -1;
            }
          })
        );

        let weekCounter = 0;

        while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
          if (weekCounter % repeatEvery === 0) {
            if (selectedDayNumbers.has(currentDate.day())) {
              occurrences++;
            }
          }

          currentDate = currentDate.add(1, "day");

          if (currentDate.day() === 0) {
            weekCounter++;
          }
        }

        return occurrences;
      }
      case "month": {
        let occurrences = 0;
        let currentDate = start;

        // Create a Set of selected day numbers (0-6, where 0 is Sunday)
        const selectedDayNumbers = new Set(
          selectedDays.map((day) => {
            switch (day) {
              case "s":
                return 0;
              case "m":
                return 1;
              case "t":
                return 2;
              case "w":
                return 3;
              case "th":
                return 4;
              case "f":
                return 5;
              case "sa":
                return 6;
              default:
                return -1;
            }
          })
        );

        let monthCounter = 0;
        const startMonth = start.month();

        while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
          // Check if we should count this month based on repeatEvery
          const currentMonth = currentDate.month();
          const monthDiff =
            currentMonth -
            startMonth +
            (currentDate.year() - start.year()) * 12;

          if (monthDiff % repeatEvery === 0) {
            // If this day is one of our selected days, increment occurrences
            if (selectedDayNumbers.has(currentDate.day())) {
              occurrences++;
            }
          }

          // Move to next day
          currentDate = currentDate.add(1, "day");
        }

        return occurrences;
      }
      default:
        return 1;
    }
  };

  // Function to save working hours to the location
  const handleSaveWorkingHours = () => {
    let workingHoursData = {
      scheduleType: "standard",
      workingDays: selectedDays.map(
        (day) => dayKeyToApiMap[day] || day.toUpperCase()
      ),
      isActive: true,
    };

    if (openAdvance) {
      let endsOnData = {
        type: endType,
      };

      if (endType === "on") {
        endsOnData.endDate = selectedEndDate.toISOString();
        const calculatedOccurrences = calculateOccurrences(
          selectedDate,
          selectedEndDate,
          repeatType,
          repeatEveryValue,
          repeatOnDays
        );
        endsOnData.occurrences = calculatedOccurrences;
      } else if (endType === "after") {
        endsOnData.occurrences = occurrenceCount;
      }

      // Create the repeat description based on type and selected days
      const getRepeatDescription = () => {
        const selectedDayNames = repeatOnDays.map((day) => dayNamesMap[day]);

        if (repeatType === "week") {
          return [
            `Repeats every ${repeatEveryValue > 1 ? repeatEveryValue : ""} ${
              repeatEveryValue > 1 ? "weeks" : "week"
            } on ${selectedDayNames.join(", ")}`,
          ];
        } else if (repeatType === "month") {
          return [
            `Repeats every ${repeatEveryValue > 1 ? repeatEveryValue : ""} ${
              repeatEveryValue > 1 ? "months" : "month"
            } on ${selectedDayNames.join(", ")}`,
          ];
        }
        return [];
      };

      workingHoursData = {
        scheduleType: "advanced",
        workingDays: repeatOnDays.map(
          (day) => dayKeyToApiMap[day] || day.toUpperCase()
        ),
        advancedSettings: {
          enabled: true,
          startDate: selectedDate.toISOString(),
          startEndTime: {
            startTime: displayStartTime,
            endTime: displayEndTime,
          },
          customRecurrence: {
            repeatEvery: repeatEveryValue,
            repeatUnit: repeatType,
            repeatOn: repeatType === "days" ? [] : getRepeatDescription(),
            endsOn: endsOnData,
          },
        },
        isActive: true,
      };
    } else if (customEnabled) {
      const customSchedule = {};
      selectedDays.forEach((dayKey) => {
        const dayName = dayNamesMap[dayKey];
        if (dayName) {
          const dayTimes = customTimes[dayName] || {};
          const startTimeToUse = dayTimes.start || startTime;
          const endTimeToUse = dayTimes.end || endTime;

          customSchedule[dayName] = {
            startTime: formatTo12Hour(startTimeToUse) || displayStartTime,
            endTime: formatTo12Hour(endTimeToUse) || displayEndTime,
          };
        }
      });

      workingHoursData = {
        scheduleType: "custom",
        workingDays: selectedDays.map(
          (day) => dayKeyToApiMap[day] || day.toUpperCase()
        ),
        customSchedule,
        isActive: true,
      };
    } else {
      // Standard schedule
      workingHoursData = {
        scheduleType: "standard",
        workingDays: selectedDays.map(
          (day) => dayKeyToApiMap[day] || day.toUpperCase()
        ),
        standardWorkHours: {
          startTime: displayStartTime,
          endTime: displayEndTime,
        },
        isActive: true,
      };
    }

    // Update the specific location with working hours data
    const updatedLocations = [...watch_location];
    updatedLocations[selectedLocationIndex] = {
      ...updatedLocations[selectedLocationIndex],
      workingHours: workingHoursData,
    };

    // Update the form value with validation
    setValue("location", updatedLocations, { shouldValidate: true });

    // Close dialog first
    onClose();
    
    // Trigger form validation after a short delay to ensure state is updated
    if (trigger) {
      setTimeout(() => {
        trigger("location");
      }, 100);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      transitionDuration={500}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottomLeftRadius: !fullScreen && 10,
          borderBottomRightRadius: !fullScreen && 10,
          p: 2,
          width: "100%",
          mx: "auto",
          mt: fullScreen ? "auto" : undefined,
          maxHeight: "80vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: "center", fontWeight: 600, position: "relative" }}
      >
        {locationData?.businessLocationName}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!openAdvance && (
          <>
            <Box textAlign="center" mb={2}>
              <Typography fontWeight={600}>working days</Typography>
              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                {daysOfWeek.map((dayKey) => (
                  <Button
                    key={dayKey}
                    variant={
                      selectedDays.includes(dayKey) ? "contained" : "outlined"
                    }
                    sx={{
                      minWidth: 32,
                      padding: "4px 6px",
                      bgcolor: selectedDays.includes(dayKey)
                        ? "#6C00E6"
                        : "#fff",
                      color: selectedDays.includes(dayKey) ? "#fff" : "#000",
                      fontWeight: 600,
                      borderRadius: "50%",
                      borderColor: "#ccc",
                      textTransform: "lowercase",
                    }}
                    onClick={() => toggleDay(dayKey)}
                  >
                    {dayKey.charAt(0)}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box display="flex" gap={2} justifyContent="center" mb={2}>
              <TextField
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setDisplayStartTime(formatTo12Hour(e.target.value));
                }}
                fullWidth
                size="small"
              />
              <TextField
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setDisplayEndTime(formatTo12Hour(e.target.value));
                }}
                fullWidth
                size="small"
              />
            </Box>

            <Box display="flex" alignItems="center" mb={1}>
              <Typography fontWeight={600}>custom</Typography>
              <Checkbox
                checked={customEnabled}
                onChange={() => setCustomEnabled((prev) => !prev)}
                sx={{ ml: 1 }}
              />
            </Box>

            {selectedDays?.map((shortDayKey) => {
              const fullDay = dayNamesMap[shortDayKey];
              return (
                <Box
                  key={fullDay}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    sx={{ textTransform: "lowercase", fontWeight: 700 }}
                  >
                    {fullDay}
                  </Typography>
                  {customEnabled ? (
                    <Box display="flex" flexDirection={"column"} gap={1}>
                      <TextField
                        type="time"
                        size="small"
                        value={customTimes?.[fullDay]?.start || startTime}
                        onChange={(e) =>
                          handleCustomTimeChange(
                            fullDay,
                            "start",
                            e.target.value
                          )
                        }
                      />
                      <TextField
                        type="time"
                        size="small"
                        value={customTimes?.[fullDay]?.end || endTime}
                        onChange={(e) =>
                          handleCustomTimeChange(fullDay, "end", e.target.value)
                        }
                      />
                      <Divider />
                    </Box>
                  ) : (
                    <Typography fontSize="0.9rem" color="#6C00E6">
                      {displayStartTime} to {displayEndTime}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        )}

        <FormControlLabel
          control={
            <Checkbox checked={openAdvance} onChange={handleAdvanceToggle} />
          }
          label={<Typography fontWeight={600}>advance settings</Typography>}
          sx={{ mt: 2 }}
        />

        {openAdvance && (
          <Box px={2}>
            <Typography fontWeight={600} mb={1} color="primary">
              advance settings
            </Typography>

            <Typography variant="body2" mb={0.5}>
              start date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box display="flex" alignItems="center" mb={2}>
                <IconButton
                  onClick={handleIconClick}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
                <Typography>{selectedDate.format("dddd, MMMM D")}</Typography>
              </Box>

              <DatePicker
                open={openDatePicker}
                onClose={handleClose}
                value={selectedDate}
                onChange={(newValue) => {
                  setSelectedDate(newValue);
                  handleClose();
                }}
                sx={{ display: "none" }}
                slotProps={{
                  popper: {
                    anchorEl: anchorEl,
                    placement: "bottom-start",
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, 8],
                        },
                      },
                    ],
                  },
                }}
              />
            </LocalizationProvider>

            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="body2" mb={0.5}>
                start/end time
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton onClick={handleTimeClick} size="small">
                  <AccessTimeIcon fontSize="small" />
                </IconButton>
                <Typography
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  onClick={handleTimeClick}
                >
                  {displayStartTime} - {displayEndTime}
                </Typography>
              </Box>
            </Box>

            {timePickerOpen && (
              <Box
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 2,
                  mb: 2,
                  border: "1px solid #ddd",
                }}
              >
                <Typography variant="body2" fontWeight={600} mb={2}>
                  select time range
                </Typography>

                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1}>
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.5, display: "block" }}
                    >
                      Start Time
                    </Typography>
                    <TextField
                      type="time"
                      value={tempStartTime}
                      onChange={(e) => setTempStartTime(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>

                  <Box flex={1}>
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.5, display: "block" }}
                    >
                      End Time
                    </Typography>
                    <TextField
                      type="time"
                      value={tempEndTime}
                      onChange={(e) => setTempEndTime(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Box>
                </Box>

                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleTimeCancel}
                    sx={{
                      bgcolor: "#6C00E6",
                      textTransform: "none",
                    }}
                  >
                    cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleTimeConfirm}
                    sx={{
                      bgcolor: "#6C00E6",
                      textTransform: "none",
                      textDecoration: "underline",
                    }}
                  >
                    confirm
                  </Button>
                </Box>
              </Box>
            )}

            <Typography fontWeight={600} mb={1}>
              custom recurrence
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                type="number"
                size="small"
                value={repeatEveryValue}
                onChange={(e) =>
                  setRepeatEveryValue(parseInt(e.target.value) || 1)
                }
                sx={{ width: 80 }}
              />
              <Select
                size="small"
                value={repeatType}
                onChange={(e) => setRepeatType(e.target.value)}
                fullWidth
              >
                <MenuItem value="days">days</MenuItem>
                <MenuItem value="week">week</MenuItem>
                <MenuItem value="month">month</MenuItem>
              </Select>
            </Box>

            {(repeatType === "week" || repeatType === "month") && (
              <Box display="flex" justifyContent="center" gap={1} mb={2}>
                {daysOfWeek.map((day, idx) => (
                  <Button
                    key={idx}
                    onClick={() => toggleDayRepeatOnDays(day)}
                    variant={
                      repeatOnDays.includes(day) ? "contained" : "outlined"
                    }
                    sx={{
                      minWidth: 32,
                      padding: "4px 6px",
                      borderRadius: "50%",
                      bgcolor: repeatOnDays.includes(day) ? "#6C00E6" : "#fff",
                      color: repeatOnDays.includes(day) ? "#fff" : "#000",
                      fontWeight: 600,
                    }}
                  >
                    {day.charAt(0)}
                  </Button>
                ))}
              </Box>
            )}

            {/* {repeatType === "month" && (
              <Box
                mb={2}
                sx={{
                  bgcolor: "#EEEEEE",
                  p: 2,
                  maxWidth: 200,
                  textAlign: "center",
                  borderRadius: "5px",
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>
                  monthly on day {selectedDate.date()}
                </Typography>
              </Box>
            )} */}

            <Typography fontWeight={600} mb={1}>
              ends on
            </Typography>
            <RadioGroup
              value={endType}
              onChange={(e) => setEndType(e.target.value)}
            >
              <FormControlLabel
                value="never"
                control={<Radio />}
                label="never"
              />

              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <FormControlLabel value="on" control={<Radio />} label="on" />
                {endType === "on" && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        bgcolor: "#EEEEEE",
                        p: 1,
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                      onClick={handleEndDateIconClick}
                    >
                      <CalendarMonthIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography>
                        {selectedEndDate.format("MMMM D, YYYY").toLowerCase()}
                      </Typography>
                    </Box>

                    <DatePicker
                      open={Boolean(endDateAnchorEl)}
                      onClose={handleEndDateClose}
                      value={selectedEndDate}
                      onChange={(newValue) => {
                        setSelectedEndDate(newValue);
                        setEndDate(newValue.format("YYYY-MM-DD"));
                        handleEndDateClose();
                      }}
                      sx={{ display: "none" }}
                      slotProps={{
                        popper: {
                          anchorEl: endDateAnchorEl,
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [0, 8],
                              },
                            },
                          ],
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <FormControlLabel
                  value="after"
                  control={<Radio />}
                  label="after"
                />
                {endType === "after" && (
                  <Select
                    size="small"
                    disabled={endType !== "after"}
                    value={occurrenceCount}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setOccurrenceCount(newValue);
                      setCalculatedOccurrences(newValue);
                    }}
                    sx={{ width: 180 }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                        },
                      },
                    }}
                  >
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                      <MenuItem key={num} value={num}>
                        {num} occurrences
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
            </RadioGroup>
            {endType !== "never" && (
              <Box
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid #ddd",
                }}
              >
                <Typography variant="body2" color="primary">
                  Total Occurrences: {calculatedOccurrences}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {`Repeating every ${repeatEveryValue} ${repeatType}`}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {`From: ${selectedDate.format("MMM D, YYYY")}`}
                  <br />
                  {endType === "on" && ( // Only show end date when endType is "on"
                    <>
                      {`To: ${selectedEndDate.format("MMM D, YYYY")}`}
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          sx={{
            bgcolor: "#6C00E6",
            mt: 2,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: "none",
          }}
          onClick={handleSaveWorkingHours}
        >
          set
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default WorkHoursDialog;
